import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Modal, Button } from 'react-bootstrap';

interface TermsAndConditionsModalProps {
  show: boolean;
  onHide: () => void;
  onAgree: () => void;
}

const TermsContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
  padding: 20px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  margin-bottom: 20px;
`;

const TermsTitle = styled.h3`
  color: #1f2937;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 20px;
  text-align: center;
`;

const TermsSection = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h4`
  color: #374151;
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 10px;
`;

const SectionText = styled.p`
  color: #4b5563;
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 15px;
  text-align: justify;
`;

const EffectiveDate = styled.div`
  text-align: center;
  color: #6b7280;
  font-size: 0.9rem;
  font-style: italic;
  margin-bottom: 20px;
  padding: 10px;
  background: #f3f4f6;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
`;

const AgreeButton = styled(Button)`
  background-color: ${props => props.disabled ? '#9ca3af' : '#3b82f6'};
  border-color: ${props => props.disabled ? '#9ca3af' : '#3b82f6'};
  color: white;
  font-weight: 600;
  padding: 10px 24px;
  border-radius: 8px;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background-color: #2563eb;
    border-color: #2563eb;
    transform: translateY(-1px);
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const ScrollIndicator = styled.div`
  text-align: center;
  color: #6b7280;
  font-size: 0.9rem;
  font-style: italic;
  margin-bottom: 15px;
`;

const TermsAndConditionsModal: React.FC<TermsAndConditionsModalProps> = ({
  show,
  onHide,
  onAgree
}) => {
  const [canAgree, setCanAgree] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [currentDate, setCurrentDate] = useState('');
  const termsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Set current date and time when component mounts
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    };
    setCurrentDate(now.toLocaleDateString('en-US', options));
  }, []);

  const handleScroll = () => {
    if (termsRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = termsRef.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
      setHasScrolledToBottom(isAtBottom);
    }
  };

  useEffect(() => {
    if (hasScrolledToBottom) {
      setCanAgree(true);
    }
  }, [hasScrolledToBottom]);

  const handleAgree = () => {
    if (canAgree) {
      onAgree();
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Terms & Conditions</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <TermsContainer ref={termsRef} onScroll={handleScroll}>
          <TermsTitle>Terms & Conditions</TermsTitle>
          
          <EffectiveDate>
            Version 1.0 | Effective Date: {currentDate}
          </EffectiveDate>
          
          <TermsSection>
            <SectionText>
              Welcome to our Agentic AI Platform ("Platform," "we," "our," or "us"). These Terms & Conditions ("Terms") govern your use of our large language model (LLM), AI agent services, and related software (the "Services"). By accessing or using the Services, you agree to be bound by these Terms. If you do not agree, you must not use the Services.
            </SectionText>
          </TermsSection>

          <TermsSection>
            <SectionTitle>1. Nature of Services</SectionTitle>
            <SectionText>
              Our Services provide AI-powered tools intended to assist clinicians, medical researchers, healthcare professionals, and life sciences organizations. The Services are for informational and research purposes only and do not constitute medical advice, diagnosis, or treatment. All clinical and medical decisions remain the sole responsibility of qualified professionals.
            </SectionText>
          </TermsSection>

          <TermsSection>
            <SectionTitle>2. Eligibility & User Obligations</SectionTitle>
            <SectionText>
              You may use the Services only if you are authorized and compliant with applicable laws and regulations. You agree not to misuse the Services, including but not limited to: attempting to reverse engineer, using outputs for self-diagnosis by non-professionals, or applying the Services in ways that may cause harm.
            </SectionText>
          </TermsSection>

          <TermsSection>
            <SectionTitle>3. Data & Privacy</SectionTitle>
            <SectionText>
              Use of the Services may involve the processing of clinical or research-related data. By using the Services, you grant us a limited license to process such data as necessary to provide the Services. We will handle personal or sensitive information in accordance with our Privacy Policy and applicable laws (including HIPAA and GDPR, where applicable).
            </SectionText>
          </TermsSection>

          <TermsSection>
            <SectionTitle>4. Intellectual Property</SectionTitle>
            <SectionText>
              All rights, title, and interest in the Services, including software, models, and documentation, are owned by the Platform. You are granted a limited, non-exclusive, revocable license to use the Services solely in accordance with these Terms.
            </SectionText>
          </TermsSection>

          <TermsSection>
            <SectionTitle>5. Disclaimers & Limitation of Liability</SectionTitle>
            <SectionText>
              The Services are provided "as is" without warranties of any kind. We do not guarantee accuracy, completeness, or fitness for a particular purpose. To the maximum extent permitted by law, we are not liable for any damages, losses, or liabilities arising from your use of or reliance on the Services.
            </SectionText>
          </TermsSection>

          <TermsSection>
            <SectionTitle>6. Indemnification</SectionTitle>
            <SectionText>
              You agree to indemnify and hold harmless the Platform, its affiliates, and employees from any claims, damages, or expenses arising out of your use of the Services, including but not limited to medical or research decisions made using AI outputs.
            </SectionText>
          </TermsSection>

          <TermsSection>
            <SectionTitle>7. Termination</SectionTitle>
            <SectionText>
              We may suspend or terminate your access to the Services at any time for misuse, non-compliance with these Terms, or other lawful reasons. You may stop using the Services at any time.
            </SectionText>
          </TermsSection>

          <TermsSection>
            <SectionTitle>8. Governing Law & Dispute Resolution</SectionTitle>
            <SectionText>
              These Terms are governed by the laws of the United States. Any disputes shall be resolved through binding arbitration or the courts of the United States.
            </SectionText>
          </TermsSection>

          <TermsSection>
            <SectionTitle>9. Changes</SectionTitle>
            <SectionText>
              We may update these Terms from time to time. Continued use of the Services after changes constitutes acceptance of the revised Terms.
            </SectionText>
          </TermsSection>
        </TermsContainer>

        {!hasScrolledToBottom && (
          <ScrollIndicator>
            Please scroll to the bottom to read all terms and conditions
          </ScrollIndicator>
        )}

        <div style={{ textAlign: 'center' }}>
          <AgreeButton 
            disabled={!canAgree}
            onClick={handleAgree}
          >
            {canAgree ? 'I Agree to Terms and Conditions' : 'Scroll to Bottom to Agree'}
          </AgreeButton>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default TermsAndConditionsModal;
